/**
 * GTM Analytics Service
 *
 * Pushes events to window.dataLayer for Google Tag Manager (server-side via stape.io).
 * Server container URL and GTM Container ID are env-configurable via VITE_SGTM_URL / VITE_GTM_CONTAINER_ID.
 *
 * All "first" events check backend flags before firing — skip silently if already triggered.
 * Guards are internal — callers do not need their own checks.
 *
 * After firing a first event, the in-memory Zustand store is updated immediately to prevent
 * re-fire within the same session. Callers must also persist the flag to the backend via
 * `usePersistFirstEventFlag()` to prevent re-fire across sessions.
 */

import { useMutation } from '@apollo/client/react';
import { UPDATE_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import type { UpdateUserSettingsInput } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

function getUser() {
  const { me } = useAuthStore.getState();
  if (!me) return null;
  return {
    userId: me._id,
    user_email: me.email,
    user_role: me.rootRole,
    accountId: me.accountId,
    userSettings: me.userSettings,
    appMetas: me.appMetas,
  };
}

/** Lookup appCategories from me.appMetas by appType, join as comma-separated string (matches scio1). */
function getAppCategory(
  appMetas: { appType?: string; appCategories?: string[] }[] | undefined,
  appType: string | undefined,
): string {
  if (!appType || !appMetas) return '';
  const meta = appMetas.find((m) => m.appType === appType);
  return (meta?.appCategories ?? []).join(',');
}

function pushEvent(event: string, extra?: Record<string, unknown>) {
  const user = getUser();
  if (!user) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    userId: user.userId,
    user_email: user.user_email,
    accountId: user.accountId,
    ...extra,
  });
}

/** Update in-memory userSettings flag to prevent re-fire within the same session. */
function markFlagInStore(flagName: string) {
  const { me } = useAuthStore.getState();
  if (!me) return;
  useAuthStore.setState({
    me: {
      ...me,
      userSettings: {
        ...(me.userSettings as Record<string, unknown>),
        [flagName]: true,
      },
    },
  });
}

// ── Public API ──────────────────────────────────────────

/** Fires on every login (fresh + session resume). */
export function trackLogin() {
  pushEvent('loginEvent');
}

/** Fires once per account — checks company.payment.firstDevicePaired (auto-set by backend). */
export function trackFirstDevicePaired() {
  const { company } = useAuthStore.getState();
  if (!company) return; // company not loaded yet — skip to avoid false positive
  if (company.payment?.firstDevicePaired) return;
  pushEvent('firstDevicePaired');
}

/** Fires once per user — checks userSettings.firstAssetCreated. Returns true if event was pushed. */
export function trackFirstAssetCreated(params: {
  type: string;
  fileType?: string;
  appType?: string;
}): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstAssetCreated) return false;

  pushEvent('firstAssetCreated', {
    user_role: user.user_role,
    assetType: params.type,
    type: params.type,
    fileType: params.fileType ?? '',
    appType: params.appType ?? '',
    appCategory: getAppCategory(user.appMetas, params.appType),
  });
  markFlagInStore('firstAssetCreated');
  return true;
}

/** Fires on every asset create (not just first). */
export function trackNewAssetCreated(params: {
  type: string;
  fileType?: string;
  appType?: string;
}) {
  const user = getUser();
  if (!user) return;

  pushEvent('newAssetCreated', {
    user_role: user.user_role,
    assetType: params.type,
    type: params.type,
    fileType: params.fileType ?? '',
    appType: params.appType ?? '',
    appCategory: getAppCategory(user.appMetas, params.appType),
  });
}

/** Fires once per user — checks userSettings.firstPlaylistCreated. Returns true if event was pushed. */
export function trackFirstPlaylistCreated(params?: {
  itemCount?: number;
  totalDuration?: number;
}): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstPlaylistCreated) return false;

  pushEvent('firstPlaylistCreated', {
    user_role: user.user_role,
    itemCount: params?.itemCount ?? 0,
    total_duration: params?.totalDuration ?? 0,
  });
  markFlagInStore('firstPlaylistCreated');
  return true;
}

/** Fires on every playlist create (not just first). */
export function trackNewPlaylistCreated(params?: { itemCount?: number; totalDuration?: number }) {
  const user = getUser();
  if (!user) return;

  pushEvent('newPlaylistCreated', {
    user_role: user.user_role,
    itemCount: params?.itemCount ?? 0,
    total_duration: params?.totalDuration ?? 0,
  });
}

/** Fires once per user — checks userSettings.firstScheduleCreated. Returns true if event was pushed. */
export function trackFirstScheduleCreated(): boolean {
  const user = getUser();
  if (!user) return false;
  if (user.userSettings?.firstScheduleCreated) return false;

  pushEvent('firstScheduleCreated', {
    user_role: user.user_role,
  });
  markFlagInStore('firstScheduleCreated');
  return true;
}

/** Fires on every schedule create (not just first). */
export function trackNewScheduleCreated() {
  const user = getUser();
  if (!user) return;

  pushEvent('newScheduleCreated', {
    user_role: user.user_role,
  });
}

// ── Persistence Hook ────────────────────────────────────

type FirstEventFlag = 'firstAssetCreated' | 'firstPlaylistCreated' | 'firstScheduleCreated';

/**
 * Hook that returns a function to persist a first-event flag to the backend.
 * Call after a `trackFirst*()` function returns `true`.
 */
export function usePersistFirstEventFlag() {
  const [mutate] = useMutation(UPDATE_USER_SETTINGS_AUTH);
  return (flag: FirstEventFlag) => {
    const updateData: UpdateUserSettingsInput = { [flag]: true };
    mutate({ variables: { updateData } }).catch(() => {
      // Silently fail — in-memory guard still prevents re-fire within session.
      // The flag will be retried on the next "first" event if the user creates again.
    });
  };
}
